import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FaLinkedin, FaGithub, FaArrowRight } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { fetchProjectMetadata, fetchBlogMetadata } from './utils/getMetadata';
import { Typewriter } from 'react-simple-typewriter';
import LightRays from './utils/LightRays';
import NetworkSphere from './utils/NetworkSphere';
import SphereWidgets from './utils/SphereWidgets';
import { useTheme } from '../contexts/ThemeContext';

/* ─── Palette ─── */
const P = {
  lightest: 'rgb(var(--gray))',
  mid:      '#6b788b',
  midDark:  '#5c6776',
  dark:     '#45526d',
  darkest:  '#121e31',
};

/* ═══ PAGE WRAPPER ═══ */

const PageWrapper = styled.div`
  position: relative;
  margin-top: -4.5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  overflow-x: hidden;
`;

const RaysLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

const GridLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: radial-gradient(circle at 1px 1px, rgb(var(--fg) / 0.05) 1px, transparent 0);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 75% 65% at center, #000 25%, transparent 90%);
  -webkit-mask-image: radial-gradient(ellipse 75% 65% at center, #000 25%, transparent 90%);
  opacity: 0.55;
`;

const PageContent = styled.div`
  position: relative;
  z-index: 1;
`;

/* ═══ HERO ═══ */

const Hero = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: stretch;
  gap: 3rem;
  padding: 4.5rem 6vw 6.5rem;
  position: relative;
  overflow: hidden;

  @media (min-width: 1400px) {
    gap: 5rem;
  }

  @media (max-width: 1100px) {
    padding: 5.5rem 4.5vw 6rem;
    gap: 2rem;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 1.5rem;
    align-items: stretch;
  }

  @media (max-width: 768px) {
    padding: 5rem 1.5rem 5.5rem;
    min-height: calc(100vh - 4.5rem);
  }
`;

const HeroLeft = styled.div`
  flex: 1 1 55%;
  max-width: 720px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex: none;
    max-width: 100%;
  }
`;

const HeroRight = styled.div`
  flex: 1 1 45%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  align-self: stretch;

  @media (min-width: 1400px) {
    margin-right: 4vw;
  }

  @media (max-width: 900px) {
    flex: none;
    align-items: stretch;
  }
`;

const SphereStage = styled.div`
  position: relative;
  width: 100%;
  max-width: 580px;
  aspect-ratio: 1 / 1;

  @media (max-width: 900px) {
    max-width: 420px;
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    max-width: 280px;
  }
`;

const HeroRole = styled(motion.p)`
  font-size: 0.92rem;
  color: rgb(var(--fg) / 0.75);
  letter-spacing: 0.3em;
  text-transform: uppercase;
  margin: 0 0 1.4rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;

  @media (max-width: 768px) {
    font-size: 0.76rem;
    letter-spacing: 0.24em;
    margin-bottom: 1rem;
  }
`;

const HeroRoleRule = styled.span`
  display: inline-block;
  width: 24px;
  height: 1px;
  background: rgb(var(--fg) / 0.45);
  margin-right: 14px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 18px;
    margin-right: 10px;
  }
`;

const HeroRoleSep = styled.span`
  margin: 0 0.6em;
  opacity: 0.5;

  @media (max-width: 480px) {
    display: none;
  }
`;

const HeroRoleMeta = styled.span`
  @media (max-width: 480px) {
    display: none;
  }
`;

const HeroName = styled(motion.h1)`
  font-size: clamp(2.6rem, 7vw, 5.5rem);
  color: rgb(var(--fg));
  font-weight: 800;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  margin: 0 0 1.6rem;
  line-height: 0.98;

  @media (max-width: 768px) {
    font-size: clamp(2.2rem, 10vw, 3.4rem);
    margin-bottom: 1.1rem;
    line-height: 1;
  }
`;

const TypewriterLine = styled(motion.div)`
  font-size: clamp(1.1rem, 1.5vw, 1.45rem);
  color: rgb(var(--fg) / 0.6);
  margin-top: 1.6rem;
  margin-bottom: 2.4rem;
  min-height: 2.2rem;
  line-height: 1.45;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-top: 1.1rem;
    margin-bottom: 1.8rem;
    min-height: 3.2rem;
    white-space: normal;
  }
`;

const TypewriterBreak = styled.br`
  display: none;
  @media (max-width: 768px) { display: block; }
`;

const TypewriterStatic = styled.span`
  margin-right: 0.3rem;
`;

const TypewriterKeyword = styled.span`
  color: rgb(var(--fg) / 0.82);
  font-weight: 600;

  @media (max-width: 768px) {
    color: rgb(var(--fg) / 0.88);
    font-weight: 700;
  }
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.9rem 1.4rem;
  margin-bottom: 2.2rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.7rem;
  }
`;

const PrimaryCta = styled.button`
  padding: 0.9rem 2.4rem;
  border: 1px solid rgb(var(--fg) / 0.16);
  border-radius: 999px;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  backdrop-filter: blur(8px);
  font-family: inherit;

  &:hover {
    background: rgb(var(--fg) / 0.12);
    border-color: rgb(var(--fg) / 0.3);
    box-shadow: 0 0 0 1px rgb(var(--fg) / 0.06), 0 12px 32px -4px rgb(0 0 0 / calc(0.4 * var(--shadow-mul)));
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 0.78rem 2rem;
    font-size: 0.78rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding-left: 0.4rem;

  @media (max-width: 480px) {
    padding-left: 0;
    justify-content: center;
  }
`;

const IconLink = styled.a`
  font-size: 1.15rem;
  color: rgb(var(--fg) / 0.5);
  transition: color 0.35s, transform 0.35s;
  display: inline-flex;
  &:hover {
    color: rgb(var(--fg) / 0.95);
    transform: translateY(-1px);
  }
`;

const SocialDot = styled.span`
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: rgb(var(--fg) / 0.14);
`;

/* ─── Hero chrome (rails + crosshairs) ─── */

const SideRail = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: none;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  font-size: 0.44rem;
  letter-spacing: 0.21em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.3);

  & > span {
    white-space: nowrap;
    display: inline-block;
  }

  @media (max-width: 1100px) {
    display: none;
  }
`;

const SideRailLeft = styled(SideRail)`
  left: 0;
  & > span { transform: rotate(-90deg); }
`;

const SideRailRight = styled(SideRail)`
  right: 0;
  & > span { transform: rotate(90deg); }
`;

const Crosshair = styled.span`
  position: absolute;
  width: 8px;
  height: 8px;
  z-index: 3;
  pointer-events: none;

  &::before, &::after {
    content: '';
    position: absolute;
    background: rgb(var(--fg) / 0.28);
  }
  &::before {
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    transform: translateX(-50%);
  }
  &::after {
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    transform: translateY(-50%);
  }

  @media (max-width: 768px) { display: none; }
`;

/* ─── Marquee (H6) ─── */

const MarqueeBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  border-top: 1px solid rgb(var(--fg) / 0.06);
  border-bottom: 1px solid rgb(var(--fg) / 0.06);
  padding: 0.85rem 0;
  pointer-events: auto;
  background: rgb(var(--fg) / 0.06);
  backdrop-filter: blur(14px) saturate(1.1);
  -webkit-backdrop-filter: blur(14px) saturate(1.1);
  z-index: 2;
  mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, #000 8%, #000 92%, transparent);

  @media (max-width: 768px) {
    padding: 0.7rem 0;
  }
`;

const MarqueeTrack = styled.div`
  display: flex;
  width: max-content;
  animation: marqueeScroll 55s linear infinite;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  font-size: 0.78rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.45);

  @keyframes marqueeScroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }

  & .item {
    padding: 0 1.6rem;
    white-space: nowrap;
  }
  & .star {
    color: rgb(var(--fg) / 0.65);
  }

  ${MarqueeBar}:hover & { animation-play-state: paused; }

  @media (max-width: 768px) {
    font-size: 0.7rem;
    & .item { padding: 0 1.1rem; }
  }
`;

/* ═══ SECTION SHELL ═══ */

const SectionWrap = styled.section`
  padding: 4.5rem 6vw;
  position: relative;

  @media (max-width: 1100px) {
    padding: 4rem 4.5vw;
  }

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

/*
 * Wider variant for sections that need to breathe close to the viewport
 * edge (used by the "What I do" card so the 5-column grid doesn't feel
 * cramped). SectionWrap's 6vw padding still provides the outer gutter.
 */
const SectionInnerWide = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-bottom: 2.4rem;
  padding-bottom: 1.4rem;
  border-bottom: 1px solid rgb(var(--fg) / 0.08);

  @media (max-width: 768px) {
    margin-bottom: 1.8rem;
    padding-bottom: 1.1rem;
  }
`;

const SectionKicker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  font-size: 0.78rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.55);
  font-weight: 600;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;

  & b {
    font-weight: 700;
    color: rgb(var(--fg) / 0.92);
  }

  & span.dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgb(var(--fg) / 0.4);
    display: inline-block;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3.6vw, 2.7rem);
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.05;
  color: rgb(var(--fg) / 0.96);
  margin: 0;

  @media (max-width: 768px) {
    font-size: clamp(1.55rem, 6vw, 2rem);
  }
`;

const SectionEyebrow = styled.div`
  font-size: 0.74rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgb(var(--gray) / 0.55);
  font-weight: 600;
`;

/* ═══ WHAT I DO ═══ */

const PracticeCard = styled.div`
  background: rgb(var(--fg) / 0.035);
  backdrop-filter: blur(28px) saturate(1.15);
  -webkit-backdrop-filter: blur(28px) saturate(1.15);
  border: 1px solid rgb(var(--fg) / 0.07);
  border-radius: 20px;
  padding: 2.4rem 2.4rem 2rem;
  box-shadow: 0 24px 60px -28px rgb(0 0 0 / calc(0.55 * var(--shadow-mul)));

  @media (max-width: 900px) {
    padding: 1.8rem 1.6rem 1.4rem;
  }

  @media (max-width: 600px) {
    padding: 1.4rem 1.2rem 1.1rem;
    border-radius: 16px;
  }
`;

const PracticeIntro = styled.p`
  font-size: clamp(1rem, 1.2vw, 1.1rem);
  line-height: 1.7;
  color: rgb(var(--fg) / 0.78);
  margin: 0 0 1.6rem;
  max-width: 78ch;

  & strong {
    color: rgb(var(--fg) / 0.95);
    font-weight: 600;
  }

  @media (max-width: 600px) {
    margin-bottom: 1.1rem;
  }
`;

const PracticeMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem 1.8rem;
  margin: 0 0 0.4rem;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.42);

  & b {
    color: rgb(var(--fg) / 0.78);
    font-weight: 600;
    margin-left: 0.55rem;
  }
`;

const PracticeDivider = styled.div`
  height: 1px;
  background: rgb(var(--fg) / 0.08);
  margin: 1.8rem -2.4rem;

  @media (max-width: 900px) { margin: 1.4rem -1.6rem; }
  @media (max-width: 600px) { margin: 1.1rem -1.2rem; }
`;

/*
 * 4-column row (one cell per practice area). Hairline borders between cells
 * give an editorial-grid feel. Negative margins extend the row flush to the
 * card's inner edges so the dividers reach the card border.
 */
const PracticeRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin: 0 -2.4rem -2rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    margin: 0 -1.6rem -1.4rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    margin: 0 -1.2rem -1.1rem;
  }
`;

const PracticeCell = styled.div`
  padding: 1.6rem 1.6rem 1.7rem;
  border-right: 1px solid rgb(var(--fg) / 0.06);
  transition: background 0.3s;

  &:nth-child(4n) { border-right: none; }

  &:hover {
    background: rgb(var(--fg) / 0.02);
  }

  @media (max-width: 900px) {
    padding: 1.3rem 1.4rem 1.4rem;
    &:nth-child(4n) { border-right: 1px solid rgb(var(--fg) / 0.06); }
    &:nth-child(2n) { border-right: none; }
    border-bottom: 1px solid rgb(var(--fg) / 0.06);
    &:nth-last-child(-n + 2) { border-bottom: none; }
  }

  @media (max-width: 600px) {
    padding: 1.1rem 1.2rem 1.2rem;
    border-right: none !important;
    border-bottom: 1px solid rgb(var(--fg) / 0.06);
    &:last-child { border-bottom: none; }
  }
`;

const PracticeCellHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.7rem;
  margin-bottom: 0.7rem;
`;

const PracticeNum = styled.span`
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  color: rgb(var(--fg) / 0.4);
  flex-shrink: 0;
`;

const PracticeCellTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: rgb(var(--fg) / 0.96);
  margin: 0;
  letter-spacing: -0.005em;
  line-height: 1.2;
`;

const PracticeCellDesc = styled.p`
  font-size: 0.86rem;
  line-height: 1.55;
  color: rgb(var(--gray) / 0.82);
  margin: 0;
`;

/* ═══ FEATURED CARD ═══ */

const FeaturedCard = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 16px;
  background: rgb(var(--fg) / 0.035);
  backdrop-filter: blur(28px) saturate(1.15);
  -webkit-backdrop-filter: blur(28px) saturate(1.15);
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-family: inherit;
  color: inherit;
  height: 100%;

  &:hover {
    border-color: rgb(var(--fg) / 0.16);
    background: rgb(var(--fg) / 0.05);
    transform: translateY(-3px);
    box-shadow: 0 24px 60px -20px rgb(0 0 0 / calc(0.55 * var(--shadow-mul)));
  }
`;

const FeaturedThumb = styled.img`
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  display: block;
  opacity: 0.92;
  transition: opacity 0.35s;
  border-bottom: 1px solid rgb(var(--fg) / 0.05);

  ${FeaturedCard}:hover & { opacity: 1; }
`;

const FeaturedBody = styled.div`
  padding: 1.15rem 1.25rem 1.3rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  flex: 1;
`;

const FeaturedTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: rgb(var(--fg) / 0.95);
  margin: 0;
  line-height: 1.25;
  letter-spacing: -0.005em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FeaturedTitleSmall = styled(FeaturedTitle)`
  font-size: 1.02rem;
`;

const FeaturedDesc = styled.p`
  font-size: 0.85rem;
  color: rgb(var(--gray) / 0.78);
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FeaturedDescSmall = styled(FeaturedDesc)`
  font-size: 0.82rem;
  -webkit-line-clamp: 2;
`;

const FeaturedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.1rem;
`;

const Tag = styled.span`
  font-size: 0.66rem;
  color: rgb(var(--gray) / 0.7);
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  letter-spacing: 0.04em;
`;

const FeaturedMeta = styled.div`
  display: flex;
  gap: 0.9rem;
  font-size: 0.74rem;
  color: rgb(var(--gray) / 0.55);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 600;
`;

const FeaturedCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.4rem;
  color: rgb(var(--fg) / 0.7);
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  svg { transition: transform 0.3s; font-size: 0.65rem; }
  ${FeaturedCard}:hover & { color: rgb(var(--fg) / 0.95); }
  ${FeaturedCard}:hover & svg { transform: translateX(4px); }
`;

const ViewAllRow = styled.div`
  margin-top: 1.1rem;
  display: flex;
  justify-content: flex-end;
`;

const ViewAllLink = styled.button`
  background: none;
  border: none;
  color: rgb(var(--gray) / 0.55);
  font-size: 0.76rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  svg { transition: transform 0.3s; font-size: 0.65rem; }
  &:hover { color: rgb(var(--fg) / 0.92); }
  &:hover svg { transform: translateX(3px); }
`;

const FeaturedSkeleton = styled.div`
  height: 320px;
  border: 1px solid rgb(var(--fg) / 0.04);
  border-radius: 16px;
  background: rgb(var(--fg) / 0.02);
`;

const FeaturedList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.4rem;
  align-items: stretch;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

/* ═══ CONSTANTS ═══ */

const keywords = [
  'Business Intelligence',
  'Data Analytics',
  'Data Engineering',
  'Machine Learning',
  'Automation',
  'Technology',
];

// Four practice areas (Automation and Engineering merged into one card).
// Tone: neutral, descriptive of what the work actually is — not pitched.
const practiceAreas = [
  {
    num: '01',
    key: 'bi',
    title: 'Business Intelligence',
    desc:
      'SQL queries against operational systems, Power BI for dashboards, ' +
      'Power Query for ad-hoc cleaning, and Excel as the presentation ' +
      'layer for reports — clean, structured tables for stakeholders. ' +
      'Sales, inventory, and operations are the usual scope, with ' +
      'e-commerce as needed.',
  },
  {
    num: '02',
    key: 'data',
    title: 'Data Analysis',
    desc:
      'Exploratory and ad-hoc analysis across sales behaviour, customer ' +
      'engagement, inventory movement, and operational bottlenecks. ' +
      'Comfortable with imperfect, real-world data; the focus is insights ' +
      'tied to specific decisions, not dashboards in isolation.',
  },
  {
    num: '03',
    key: 'tools',
    title: 'Internal Tools & Automation',
    desc:
      'Apps Script workflows, email and registration systems, custom apps, ' +
      'websites, and similar — anything that removes repetitive or minor ' +
      'manual work. Small full-stack builds with the help of agentic and ' +
      'LLM tooling, where the goal is shipping useful internal infrastructure ' +
      'rather than building commercial software (although security is ' +
      'always a focus).',
  },
  {
    num: '04',
    key: 'ops',
    title: 'Operations & Notes',
    desc:
      'Looking at processes — where manual steps can be reduced, ownership ' +
      'clarified, feedback loops shortened. The blog runs alongside as a ' +
      'more personal space — short notes on books and topics I\'m reading, ' +
      'not a record of the work itself.',
  },
];

// Aggregate unique tags across all projects, preserving first-seen order
const collectMarqueeTags = (projects) => {
  if (!projects || !projects.length) return [];
  const seen = new Set();
  const out = [];
  projects.forEach((p) => {
    (p.tags || []).forEach((raw) => {
      const tag = String(raw).trim();
      if (!tag) return;
      const key = tag.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(tag);
    });
  });
  return out;
};

/* ═══ COMPONENT ═══ */

const Home = () => {
  const [latestProjects, setLatestProjects] = useState(null);
  const [latestPosts, setLatestPosts] = useState(null);
  const [marqueeTags, setMarqueeTags] = useState([]);
  const [showTypewriter, setShowTypewriter] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const sphereHoverRef = useRef(false);
  // Shared bus: NetworkSphere writes the projected 2D position of each
  // node into this array each frame; SphereWidgets reads it to anchor
  // the floating pipeline cards to a moving sphere node.
  const nodePositionsRef = useRef([]);
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Theme-aware LightRays colours.
  //   Dark mode: original muted slate rays on a near-black base.
  //   Light mode: meaningfully darker slate rays on the page bg so the
  //   "light source from lower-left" actually reads — the previous values
  //   produced too-faint rays. The contrast magnitude (Δ ~80–100 per
  //   channel from base) now mirrors what dark mode achieves with its
  //   #45526d/#6b788b ray colours.
  const raysColorA = theme === 'dark' ? P.dark : '#94a0b5';
  const raysColorB = theme === 'dark' ? P.mid  : '#b0b8c8';
  const raysBase   = theme === 'dark' ? '#020408' : '#f6f7f9';

  useEffect(() => {
    const t = setTimeout(() => setShowTypewriter(true), 500);
    return () => clearTimeout(t);
  }, []);

  // Honor prefers-reduced-motion for the sphere + widget animations.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReducedMotion(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  const handleSphereEnter = useCallback(() => { sphereHoverRef.current = true; }, []);
  const handleSphereLeave = useCallback(() => { sphereHoverRef.current = false; }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [projects, blog] = await Promise.all([
        fetchProjectMetadata(),
        fetchBlogMetadata(),
      ]);
      if (cancelled) return;
      if (projects && projects.length) {
        setLatestProjects(projects.slice(0, 4));
        setMarqueeTags(collectMarqueeTags(projects));
      }
      if (blog && blog.length) setLatestPosts(blog.slice(0, 4));
    })();
    return () => { cancelled = true; };
  }, []);

  const handleNav = useCallback((path) => navigate(path), [navigate]);

  return (
    <PageWrapper>
      <GridLayer />
      <RaysLayer>
        <LightRays
          colorA={raysColorA}
          colorB={raysColorB}
          base={raysBase}
          style={{ position: 'absolute', inset: 0 }}
        />
      </RaysLayer>

      <PageContent>
        <Hero>
          <SideRailLeft><span>PORTFOLIO — V.2026.05</span></SideRailLeft>
          <SideRailRight><span>LAT 37.98° N · LON 23.72° E · ATHENS</span></SideRailRight>

          <Crosshair style={{ top: '5rem', left: '1.5rem' }} />
          <Crosshair style={{ top: '5rem', right: '1.5rem' }} />
          <Crosshair style={{ bottom: '7rem', left: '1.5rem' }} />
          <Crosshair style={{ bottom: '7rem', right: '1.5rem' }} />

          <HeroLeft>
            <HeroRole
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.1 }}
            >
              <HeroRoleRule />
              <span>BI Analyst</span>
              <HeroRoleSep>·</HeroRoleSep>
              <HeroRoleMeta>Athens, GR</HeroRoleMeta>
            </HeroRole>
            <HeroName
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
            >
              Kyriakos<br />Papadopoulos
            </HeroName>
            <TypewriterLine
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <TypewriterStatic>Enhancing strategic decisions with</TypewriterStatic>
              <TypewriterBreak />
              <TypewriterKeyword>
                {showTypewriter ? (
                  <Typewriter
                    words={keywords}
                    loop={1}
                    cursor
                    cursorStyle=""
                    typeSpeed={80}
                    deleteSpeed={40}
                    delaySpeed={1500}
                  />
                ) : (
                  <span></span>
                )}
              </TypewriterKeyword>
            </TypewriterLine>
            <CtaRow>
              <PrimaryCta onClick={() => handleNav('/projects')}>
                Explore Work
              </PrimaryCta>
              <SocialLinks>
                <IconLink
                  href="https://www.linkedin.com/in/papadopoulos-kyriakos/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin />
                </IconLink>
                <SocialDot />
                <IconLink
                  href="https://github.com/kyriakos-papadopoulos"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                >
                  <FaGithub />
                </IconLink>
              </SocialLinks>
            </CtaRow>
          </HeroLeft>
          <HeroRight>
            <SphereStage
              onMouseEnter={handleSphereEnter}
              onMouseLeave={handleSphereLeave}
            >
              <NetworkSphere
                hoverActiveRef={sphereHoverRef}
                reducedMotion={reducedMotion}
                theme={theme}
                nodePositionsRef={nodePositionsRef}
              />
              <SphereWidgets
                hoverActiveRef={sphereHoverRef}
                reducedMotion={reducedMotion}
                nodePositionsRef={nodePositionsRef}
              />
            </SphereStage>
          </HeroRight>

          {marqueeTags.length > 0 && (
            <MarqueeBar>
              <MarqueeTrack>
                {[...marqueeTags, '★', ...marqueeTags, '★'].map((item, i) => (
                  <span key={i} className={item === '★' ? 'item star' : 'item'}>
                    {item}
                  </span>
                ))}
              </MarqueeTrack>
            </MarqueeBar>
          )}
        </Hero>

        <SectionWrap>
          <SectionInnerWide>
            <SectionHeader>
              <SectionKicker>
                <span>/01</span>
                <span className="dot" />
                <b>Practice</b>
              </SectionKicker>
              <SectionTitle>What I do</SectionTitle>
            </SectionHeader>
            <PracticeCard>
              <PracticeIntro>
                Background spanning business administration, business
                intelligence, data analysis, automation, and practical
                software development. Most of the work translates messy
                business needs into structured outputs — reports,
                automations, internal tools, analyses. Currently applied
                inside a family-owned publishing company across BI,
                reporting, internal tooling, and process improvement,
                without a dedicated data or technology department.
              </PracticeIntro>
              <PracticeMeta>
                <span>Stack <b>Excel · Power BI · SQL · Python · Apps Script</b></span>
                <span>Focus <b>BI · Automation · Internal tools</b></span>
                <span>Based <b>Athens, GR</b></span>
              </PracticeMeta>
              <PracticeDivider />
              <PracticeRow>
                {practiceAreas.map((p) => (
                  <PracticeCell key={p.key}>
                    <PracticeCellHead>
                      <PracticeNum>{p.num}</PracticeNum>
                      <PracticeCellTitle>{p.title}</PracticeCellTitle>
                    </PracticeCellHead>
                    <PracticeCellDesc>{p.desc}</PracticeCellDesc>
                  </PracticeCell>
                ))}
              </PracticeRow>
            </PracticeCard>
          </SectionInnerWide>
        </SectionWrap>

        <SectionWrap>
          <SectionInnerWide>
            <SectionHeader>
              <SectionKicker>
                <span>/02</span>
                <span className="dot" />
                <b>Selected work</b>
              </SectionKicker>
              <SectionTitle>Latest projects</SectionTitle>
            </SectionHeader>
            {latestProjects ? (
              <FeaturedList>
                {latestProjects.map((project) => (
                  <FeaturedCard key={project.path} onClick={() => handleNav(project.path)}>
                    {project.coverImage && (
                      <FeaturedThumb
                        src={project.coverImage}
                        alt={project.title}
                        loading="lazy"
                      />
                    )}
                    <FeaturedBody>
                      <FeaturedTitle>{project.title}</FeaturedTitle>
                      <FeaturedDesc>{project.description}</FeaturedDesc>
                      {project.tags && project.tags.length > 0 && (
                        <FeaturedTags>
                          {project.tags.slice(0, 4).map((t) => (
                            <Tag key={t}>{t}</Tag>
                          ))}
                        </FeaturedTags>
                      )}
                      <FeaturedCta>
                        Read case study <FaArrowRight />
                      </FeaturedCta>
                    </FeaturedBody>
                  </FeaturedCard>
                ))}
              </FeaturedList>
            ) : (
              <FeaturedList>
                <FeaturedSkeleton />
                <FeaturedSkeleton />
                <FeaturedSkeleton />
                <FeaturedSkeleton />
              </FeaturedList>
            )}
            <ViewAllRow>
              <ViewAllLink onClick={() => handleNav('/projects')}>
                View all projects <FaArrowRight />
              </ViewAllLink>
            </ViewAllRow>
          </SectionInnerWide>
        </SectionWrap>

        <SectionWrap style={{ paddingBottom: '8rem' }}>
          <SectionInnerWide>
            <SectionHeader>
              <SectionKicker>
                <span>/03</span>
                <span className="dot" />
                <b>Reading log</b>
              </SectionKicker>
              <SectionTitle>Recent reads</SectionTitle>
            </SectionHeader>
            {latestPosts ? (
              <FeaturedList>
                {latestPosts.map((post) => (
                  <FeaturedCard key={post.path} onClick={() => handleNav(post.path)}>
                    {post.coverImage && (
                      <FeaturedThumb
                        src={post.coverImage}
                        alt={post.title}
                        loading="lazy"
                      />
                    )}
                    <FeaturedBody>
                      {post.datePublished && (
                        <FeaturedMeta>
                          <span>{post.datePublished}</span>
                        </FeaturedMeta>
                      )}
                      <FeaturedTitleSmall>{post.title}</FeaturedTitleSmall>
                      <FeaturedDescSmall>{post.description}</FeaturedDescSmall>
                      <FeaturedCta>
                        Read note <FaArrowRight />
                      </FeaturedCta>
                    </FeaturedBody>
                  </FeaturedCard>
                ))}
              </FeaturedList>
            ) : (
              <FeaturedList>
                <FeaturedSkeleton />
                <FeaturedSkeleton />
                <FeaturedSkeleton />
                <FeaturedSkeleton />
              </FeaturedList>
            )}
            <ViewAllRow>
              <ViewAllLink onClick={() => handleNav('/blog')}>
                More from the blog <FaArrowRight />
              </ViewAllLink>
            </ViewAllRow>
          </SectionInnerWide>
        </SectionWrap>
      </PageContent>
    </PageWrapper>
  );
};

export default React.memo(Home);
